/**
 * Cyphex Teams — the search query language.
 *
 * ONE parser, shared by apps/api (which compiles the parse into SQL predicates)
 * and apps/web (which renders it back as removable chips). A second
 * implementation would be a second grammar, and the chips would eventually
 * describe a query different from the one that produced the results.
 *
 * GRAMMAR
 *   term                     bare word, folded into the free-text query
 *   "exact phrase"           phrase match
 *   -term  -"phrase"         exclusion
 *   -from:@bot               NEGATED OPERATOR — everything except
 *   from:@priya              author
 *   in:#general              channel / conversation
 *   with:@sam                conversations that include this person
 *   has:link|file|image|…    content facets
 *   is:saved|unread|pinned|… state facets
 *   kind:post|poll|voice|…   message kind
 *   type:pdf|image|…         file class (Files scope)
 *   before:  after:  on:     dates — 2026-01-31 | 2026-01 | 2026 |
 *                            today | yesterday | 7d | 3w | 6m | 1y
 *
 * EVERY token carries its exact source span, so removing a chip is a pure
 * splice on the original string. The UI never re-serialises a query and can
 * therefore never lose the parts of it the UI does not understand.
 *
 * The parser NEVER throws. Nonsense (`has:banana`, `before:soon`, an unclosed
 * quote) becomes a token flagged `valid: false`, which the UI renders as a
 * warning chip and the API ignores. A search box that rejects input is a search
 * box people stop using.
 */

/* ---------------------------------------------------------------------------
 * Vocabulary
 * ------------------------------------------------------------------------- */

export const SEARCH_OPERATORS = [
  'from',
  'in',
  'with',
  'has',
  'is',
  'kind',
  'type',
  'before',
  'after',
  'on',
] as const;
export type SearchOperator = (typeof SEARCH_OPERATORS)[number];

/**
 * One line per operator, for the completion menu.
 *
 * Here rather than in the UI because `/search/operators` serves it too: a
 * person reading the dropdown and an integrator reading the catalogue endpoint
 * must be told the same thing about the same word.
 */
export const OPERATOR_HELP: Readonly<Record<SearchOperator, string>> = {
  from: 'Author, uploader or organiser',
  in: 'Channel or conversation',
  with: 'Conversations including this person',
  has: 'Message contains',
  is: 'State — saved, unread, pinned…',
  kind: 'Message kind',
  type: 'File type',
  before: 'Older than a date',
  after: 'Newer than a date',
  on: 'One day, month or year',
};

/**
 * Date shorthands worth a dropdown row.
 *
 * The parser accepts far more (`2026-03-15`, `18m`); these are the common ones,
 * and every value here is one the grammar genuinely resolves. A menu that
 * offers a token the parser then flags invalid is worse than no menu at all.
 */
export const DATE_SUGGESTIONS: readonly { value: string; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '3m', label: 'Last 3 months' },
  { value: '1y', label: 'Last year' },
];

/**
 * Operators that accept a leading `-`.
 *
 * The date operators are excluded on purpose. `-before:2026-01` is ambiguous in
 * the way that matters: it reads as "not before January", which is `after:`
 * spelled backwards, and a user who typed it would reasonably expect either
 * that or "exclude the January window". Rather than pick one and be wrong half
 * the time, the token is flagged invalid and the UI says so.
 */
export const NEGATABLE_OPERATORS = ['from', 'in', 'with', 'has', 'is', 'kind', 'type'] as const;
export type NegatableOperator = (typeof NEGATABLE_OPERATORS)[number];

/** `kind:` — mirrors the `cyphex.message_kind` enum. */
export const KIND_VALUES = [
  'text',
  'post',
  'announcement',
  'praise',
  'poll',
  'voice',
  'file',
  'card',
  'meeting_invite',
  'system',
] as const;
export type KindValue = (typeof KIND_VALUES)[number];

export const HAS_VALUES = [
  'link',
  'file',
  'attachment',
  'image',
  'video',
  'audio',
  'voice',
  'pdf',
  'doc',
  'sheet',
  'slide',
  'archive',
  'code',
  'poll',
] as const;
export type HasValue = (typeof HAS_VALUES)[number];

export const IS_VALUES = [
  'saved',
  'unread',
  'pinned',
  'important',
  'urgent',
  'thread',
  'mention',
] as const;
export type IsValue = (typeof IS_VALUES)[number];

export const TYPE_VALUES = [
  'pdf',
  'doc',
  'sheet',
  'slide',
  'image',
  'video',
  'audio',
  'archive',
  'code',
  'notebook',
  'other',
] as const;
export type TypeValue = (typeof TYPE_VALUES)[number];

/**
 * Snippet highlight sentinels.
 *
 * The API marks matched terms with these two control characters rather than
 * `<mark>`: `ts_headline` does not escape its input, so an HTML payload would
 * hand every message author an XSS primitive on the search page. U+0001/U+0002
 * cannot occur in a real message and are stripped from the source before
 * headlining, so a message cannot forge a highlight either.
 */
export const HIGHLIGHT_START = '\u0001';
export const HIGHLIGHT_END = '\u0002';

/* ---------------------------------------------------------------------------
 * Shapes
 * ------------------------------------------------------------------------- */

export type SearchTokenKind = 'operator' | 'phrase' | 'term';

export interface SearchToken {
  /** Stable within one parse — safe as a React key. */
  id: string;
  kind: SearchTokenKind;
  operator: SearchOperator | null;
  /** Normalised value: handles lose `@`, channels lose `#`, case preserved. */
  value: string;
  /** Exact source text including any `-` prefix and quotes. */
  raw: string;
  /** Half-open span `[start, end)` into the source string. */
  start: number;
  end: number;
  negated: boolean;
  /** `false` for `has:banana`. Rendered as a warning chip, ignored by the API. */
  valid: boolean;
  /** Chip caption, e.g. `from: @priya`. */
  label: string;
}

export interface ParsedOperators {
  from: string[];
  in: string[];
  with: string[];
  has: HasValue[];
  is: IsValue[];
  kind: KindValue[];
  type: TypeValue[];
  before: string | null;
  after: string | null;
  on: string | null;
}

/**
 * The negated half of the query — `-from:@bot`, `-in:#noise`, `-has:link`.
 *
 * A SEPARATE bucket, not a flag on the positive one. `from:@priya -from:@bot`
 * is a legitimate query and both halves have to survive; folding negation into
 * `ParsedOperators` would mean one of them silently wins.
 *
 * The date operators are absent by construction — see NEGATABLE_OPERATORS.
 */
export type ParsedExclusions = Omit<ParsedOperators, 'before' | 'after' | 'on'>;

export interface ParsedQuery {
  source: string;
  tokens: SearchToken[];
  /** Free text handed to `websearch_to_tsquery` — phrases and `-` preserved. */
  text: string;
  operators: ParsedOperators;
  /** Operators the user negated. AND-ed as exclusions, never as filters. */
  exclusions: ParsedExclusions;
  /** Human-readable notes about tokens that could not be honoured. */
  warnings: string[];
  /** True when the query has neither free text nor a usable operator. */
  isEmpty: boolean;
}

/** Half-open UTC instant range. */
export interface DateSpan {
  from: string;
  to: string;
}

/* ---------------------------------------------------------------------------
 * Scanner
 * ------------------------------------------------------------------------- */

interface RawToken {
  raw: string;
  start: number;
  end: number;
}

const WHITESPACE = /\s/;

/**
 * Splits on whitespace, except inside double quotes. An unterminated quote runs
 * to the end of the input, which is what a user mid-typing expects.
 */
function scan(source: string): RawToken[] {
  const out: RawToken[] = [];
  let index = 0;

  while (index < source.length) {
    while (index < source.length && WHITESPACE.test(source.charAt(index))) index += 1;
    if (index >= source.length) break;

    const start = index;
    let quoted = false;
    while (index < source.length) {
      const char = source.charAt(index);
      if (char === '"') {
        quoted = !quoted;
        index += 1;
        continue;
      }
      if (!quoted && WHITESPACE.test(char)) break;
      index += 1;
    }
    out.push({ raw: source.slice(start, index), start, end: index });
  }
  return out;
}

const OPERATOR_SET: ReadonlySet<string> = new Set<string>(SEARCH_OPERATORS);
const HAS_SET: ReadonlySet<string> = new Set<string>(HAS_VALUES);
const IS_SET: ReadonlySet<string> = new Set<string>(IS_VALUES);
const KIND_SET: ReadonlySet<string> = new Set<string>(KIND_VALUES);
const TYPE_SET: ReadonlySet<string> = new Set<string>(TYPE_VALUES);

/** Index of the `:` that separates an operator name from its value, or -1. */
function operatorColon(body: string): number {
  const colon = body.indexOf(':');
  if (colon <= 0) return -1;
  const head = body.slice(0, colon);
  if (!/^[a-zA-Z]+$/.test(head)) return -1;
  return OPERATOR_SET.has(head.toLowerCase()) ? colon : -1;
}

function unquote(value: string): string {
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    return value.slice(1, -1);
  }
  // Unterminated: drop the opening quote so `from:"Priya` still resolves.
  return value.startsWith('"') ? value.slice(1) : value;
}

function stripSigil(value: string): string {
  return value.startsWith('@') || value.startsWith('#') ? value.slice(1) : value;
}

/* ---------------------------------------------------------------------------
 * Dates
 * ------------------------------------------------------------------------- */

const ABSOLUTE = /^(\d{4})(?:-(\d{1,2}))?(?:-(\d{1,2}))?$/;
const RELATIVE = /^(\d{1,4})([dwmy])$/i;

const utc = (year: number, month: number, day: number): Date =>
  new Date(Date.UTC(year, month, day, 0, 0, 0, 0));

function startOfUtcDay(at: Date): Date {
  return utc(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate());
}

/**
 * Resolves one date expression to a half-open UTC span.
 *
 * Absolute expressions denote the calendar period they name: `2026` is the
 * whole year, `2026-03` the month, `2026-03-15` the day. Relative expressions
 * (`7d`) span from that far back up to now. Returns `null` for anything
 * unparseable — the caller flags the token invalid rather than guessing.
 */
export function parseDateExpression(value: string, now: Date = new Date()): DateSpan | null {
  const text = value.trim().toLowerCase();
  if (text === '') return null;

  if (text === 'today') {
    const start = startOfUtcDay(now);
    return { from: start.toISOString(), to: new Date(start.getTime() + 86_400_000).toISOString() };
  }
  if (text === 'yesterday') {
    const start = new Date(startOfUtcDay(now).getTime() - 86_400_000);
    return { from: start.toISOString(), to: new Date(start.getTime() + 86_400_000).toISOString() };
  }

  const relative = RELATIVE.exec(text);
  if (relative) {
    const amount = Number(relative[1]);
    const unit = (relative[2] ?? 'd').toLowerCase();
    if (!Number.isFinite(amount) || amount <= 0) return null;
    const from = new Date(now.getTime());
    if (unit === 'd') from.setUTCDate(from.getUTCDate() - amount);
    else if (unit === 'w') from.setUTCDate(from.getUTCDate() - amount * 7);
    else if (unit === 'm') from.setUTCMonth(from.getUTCMonth() - amount);
    else from.setUTCFullYear(from.getUTCFullYear() - amount);
    return { from: from.toISOString(), to: now.toISOString() };
  }

  const absolute = ABSOLUTE.exec(text);
  if (!absolute) return null;
  const year = Number(absolute[1]);
  if (!Number.isFinite(year) || year < 1970 || year > 2999) return null;

  if (absolute[2] === undefined) {
    return { from: utc(year, 0, 1).toISOString(), to: utc(year + 1, 0, 1).toISOString() };
  }
  const month = Number(absolute[2]) - 1;
  if (month < 0 || month > 11) return null;

  if (absolute[3] === undefined) {
    return { from: utc(year, month, 1).toISOString(), to: utc(year, month + 1, 1).toISOString() };
  }
  const day = Number(absolute[3]);
  if (day < 1 || day > 31) return null;
  const start = utc(year, month, day);
  // Reject 2026-02-31 rather than silently sliding into March.
  if (start.getUTCMonth() !== month || start.getUTCDate() !== day) return null;
  return { from: start.toISOString(), to: utc(year, month, day + 1).toISOString() };
}

/**
 * Folds `before:`/`after:`/`on:` into one half-open range.
 *
 * `after:X` starts at the beginning of X (inclusive — "after March" plainly
 * includes March). `before:X` ends where X begins. `on:X` is exactly X. When
 * several are present the narrowest window wins.
 */
export function resolveDateRange(
  operators: ParsedOperators,
  now: Date = new Date(),
): { from: string | null; to: string | null } {
  let from: string | null = null;
  let to: string | null = null;

  const widenFrom = (value: string): void => {
    if (from === null || value > from) from = value;
  };
  const narrowTo = (value: string): void => {
    if (to === null || value < to) to = value;
  };

  if (operators.after !== null) {
    const span = parseDateExpression(operators.after, now);
    if (span) widenFrom(span.from);
  }
  if (operators.before !== null) {
    const span = parseDateExpression(operators.before, now);
    if (span) narrowTo(span.from);
  }
  if (operators.on !== null) {
    const span = parseDateExpression(operators.on, now);
    if (span) {
      widenFrom(span.from);
      narrowTo(span.to);
    }
  }
  return { from, to };
}

/* ---------------------------------------------------------------------------
 * Parser
 * ------------------------------------------------------------------------- */

function emptyOperators(): ParsedOperators {
  return {
    from: [],
    in: [],
    with: [],
    has: [],
    is: [],
    kind: [],
    type: [],
    before: null,
    after: null,
    on: null,
  };
}

function emptyExclusions(): ParsedExclusions {
  return { from: [], in: [], with: [], has: [], is: [], kind: [], type: [] };
}

const NEGATABLE_SET: ReadonlySet<string> = new Set<string>(NEGATABLE_OPERATORS);

export function isNegatable(operator: SearchOperator): operator is NegatableOperator {
  return NEGATABLE_SET.has(operator);
}

function labelFor(operator: SearchOperator | null, value: string, negated: boolean): string {
  const prefix = negated ? 'not ' : '';
  if (operator === null) return `${prefix}${value}`;
  if (operator === 'from' || operator === 'with') return `${prefix}${operator}: @${value}`;
  if (operator === 'in') return `${prefix}in: #${value}`;
  return `${prefix}${operator}: ${value}`;
}

function isOperatorValid(
  operator: SearchOperator,
  value: string,
  now: Date,
  negated: boolean,
): boolean {
  if (value === '') return false;
  // `-before:`/`-after:`/`-on:` have no single honest meaning; refuse rather
  // than guess. Everything else negates cleanly.
  if (negated && !isNegatable(operator)) return false;
  switch (operator) {
    case 'has':
      return HAS_SET.has(value.toLowerCase());
    case 'is':
      return IS_SET.has(value.toLowerCase());
    case 'kind':
      return KIND_SET.has(value.toLowerCase());
    case 'type':
      return TYPE_SET.has(value.toLowerCase());
    case 'before':
    case 'after':
    case 'on':
      return parseDateExpression(value, now) !== null;
    default:
      return true;
  }
}

/** Appends `value` to `bucket` unless an equal (case-insensitive) entry is there. */
function pushUnique<T extends string>(bucket: T[], value: string): void {
  const lower = value.toLowerCase();
  if (!bucket.some((entry) => entry.toLowerCase() === lower)) bucket.push(value as T);
}

/**
 * Files a valid token into the positive bucket or, when it was written with a
 * leading `-`, the exclusion bucket.
 *
 * The date operators are single-valued (last one wins) and never negated, so
 * they only ever touch `operators`.
 */
function collect(
  operators: ParsedOperators,
  exclusions: ParsedExclusions,
  token: SearchToken,
): void {
  const value = token.value;
  const lower = value.toLowerCase();

  if (token.negated) {
    switch (token.operator) {
      case 'from':
        pushUnique(exclusions.from, value);
        break;
      case 'in':
        pushUnique(exclusions.in, value);
        break;
      case 'with':
        pushUnique(exclusions.with, value);
        break;
      case 'has':
        pushUnique(exclusions.has, lower);
        break;
      case 'is':
        pushUnique(exclusions.is, lower);
        break;
      case 'kind':
        pushUnique(exclusions.kind, lower);
        break;
      case 'type':
        pushUnique(exclusions.type, lower);
        break;
      default:
        break;
    }
    return;
  }

  switch (token.operator) {
    case 'from':
      pushUnique(operators.from, value);
      break;
    case 'in':
      pushUnique(operators.in, value);
      break;
    case 'with':
      pushUnique(operators.with, value);
      break;
    case 'has':
      pushUnique(operators.has, lower);
      break;
    case 'is':
      pushUnique(operators.is, lower);
      break;
    case 'kind':
      pushUnique(operators.kind, lower);
      break;
    case 'type':
      pushUnique(operators.type, lower);
      break;
    case 'before':
      operators.before = value;
      break;
    case 'after':
      operators.after = value;
      break;
    case 'on':
      operators.on = value;
      break;
    default:
      break;
  }
}

/** Parses a raw search box string. Never throws. */
export function parseSearchQuery(source: string, now: Date = new Date()): ParsedQuery {
  const tokens: SearchToken[] = [];
  const operators = emptyOperators();
  const exclusions = emptyExclusions();
  const warnings: string[] = [];
  const textParts: string[] = [];

  scan(source).forEach((raw, index) => {
    let body = raw.raw;
    let negated = false;
    if (body.length > 1 && body.startsWith('-')) {
      negated = true;
      body = body.slice(1);
    }

    const colon = operatorColon(body);
    if (colon >= 0) {
      const operator = body.slice(0, colon).toLowerCase() as SearchOperator;
      const value = stripSigil(unquote(body.slice(colon + 1)));
      const valid = isOperatorValid(operator, value, now, negated);
      const token: SearchToken = {
        id: `t${index}-${negated ? 'not-' : ''}${operator}-${value}`,
        kind: 'operator',
        operator,
        value,
        raw: raw.raw,
        start: raw.start,
        end: raw.end,
        negated,
        valid,
        label: labelFor(operator, value, negated),
      };
      tokens.push(token);
      if (valid) collect(operators, exclusions, token);
      else if (negated && !isNegatable(operator)) {
        warnings.push(`Ignored “${raw.raw}” — ${operator}: cannot be negated. Use after:/before:.`);
      } else {
        warnings.push(`Ignored “${raw.raw}” — ${operator}: does not accept “${value}”.`);
      }
      return;
    }

    const phrase = body.startsWith('"');
    const value = phrase ? unquote(body) : body;
    if (value === '') return;
    tokens.push({
      id: `t${index}-${value}`,
      kind: phrase ? 'phrase' : 'term',
      operator: null,
      value,
      raw: raw.raw,
      start: raw.start,
      end: raw.end,
      negated,
      valid: true,
      label: labelFor(null, phrase ? `"${value}"` : value, negated),
    });
    // websearch_to_tsquery understands `-word` and `"phrase"` natively, so the
    // free text is handed over verbatim rather than re-encoded.
    textParts.push(raw.raw);
  });

  const text = textParts.join(' ');
  const hasOperator =
    operators.from.length > 0 ||
    operators.in.length > 0 ||
    operators.with.length > 0 ||
    operators.has.length > 0 ||
    operators.is.length > 0 ||
    operators.kind.length > 0 ||
    operators.type.length > 0 ||
    operators.before !== null ||
    operators.after !== null ||
    operators.on !== null ||
    // An exclusion-only query ("-from:@bot") is a real search: everything the
    // caller can see minus one author. It is not empty.
    hasExclusion(exclusions);

  return {
    source,
    tokens,
    text,
    operators,
    exclusions,
    warnings,
    isEmpty: text.trim() === '' && !hasOperator,
  };
}

/** True when any exclusion bucket is non-empty. */
export function hasExclusion(exclusions: ParsedExclusions): boolean {
  return (
    exclusions.from.length > 0 ||
    exclusions.in.length > 0 ||
    exclusions.with.length > 0 ||
    exclusions.has.length > 0 ||
    exclusions.is.length > 0 ||
    exclusions.kind.length > 0 ||
    exclusions.type.length > 0
  );
}

/** True when the query uses any operator at all — the Ctrl+K palette's cue. */
export function hasOperators(parsed: ParsedQuery): boolean {
  return parsed.tokens.some((token) => token.kind === 'operator');
}

/* ---------------------------------------------------------------------------
 * Editing helpers — the chips are removable, and facets are clickable
 * ------------------------------------------------------------------------- */

/** Removes one token from the source string by splicing out its exact span. */
export function removeSearchToken(source: string, token: SearchToken): string {
  const head = source.slice(0, token.start);
  const tail = source.slice(token.end);
  return `${head}${tail}`.replace(/\s{2,}/g, ' ').trim();
}

/** Quotes a value only when it needs it. */
export function quoteIfNeeded(value: string): string {
  return /[\s"]/.test(value) ? `"${value.replace(/"/g, '')}"` : value;
}

/**
 * Adds (or replaces, for the single-valued date operators) an operator.
 * Used when a facet is clicked: the facet becomes a chip, so the search box
 * always shows the truth about the query that ran.
 */
export function withOperator(
  source: string,
  operator: SearchOperator,
  value: string,
  now: Date = new Date(),
): string {
  const parsed = parseSearchQuery(source, now);
  const single = operator === 'before' || operator === 'after' || operator === 'on';
  const sigil = operator === 'from' || operator === 'with' ? '@' : operator === 'in' ? '#' : '';
  const next = `${operator}:${sigil}${quoteIfNeeded(value)}`;

  const existing = parsed.tokens.filter(
    (token) =>
      token.operator === operator && (single || token.value.toLowerCase() === value.toLowerCase()),
  );
  if (existing.length === 0) return `${source.trim()} ${next}`.trim();

  // Splice back-to-front so earlier spans stay valid.
  let result = source;
  for (const token of [...existing].sort((a, b) => b.start - a.start)) {
    result = `${result.slice(0, token.start)}${result.slice(token.end)}`;
  }
  return `${result.replace(/\s{2,}/g, ' ').trim()} ${next}`.trim();
}

/** Toggles an operator/value pair — the interaction every facet checkbox wants. */
export function toggleOperator(
  source: string,
  operator: SearchOperator,
  value: string,
  now: Date = new Date(),
): string {
  const parsed = parseSearchQuery(source, now);
  // Only a POSITIVE token counts as "already on". A facet whose bucket is
  // excluded renders unselected, so clicking it must add the filter — not
  // quietly delete the `-` the user typed.
  const present = parsed.tokens.find(
    (token) =>
      token.operator === operator &&
      !token.negated &&
      token.value.toLowerCase() === value.toLowerCase(),
  );
  return present ? removeSearchToken(source, present) : withOperator(source, operator, value, now);
}

/**
 * Replaces `operator:value` with `-operator:value` (or adds the exclusion).
 * The "mute this author / channel" affordance on a facet row.
 */
export function excludeOperator(
  source: string,
  operator: NegatableOperator,
  value: string,
  now: Date = new Date(),
): string {
  const parsed = parseSearchQuery(source, now);
  const matching = parsed.tokens.filter(
    (token) => token.operator === operator && token.value.toLowerCase() === value.toLowerCase(),
  );
  let result = source;
  for (const token of [...matching].sort((a, b) => b.start - a.start)) {
    result = `${result.slice(0, token.start)}${result.slice(token.end)}`;
  }
  const sigil = operator === 'from' || operator === 'with' ? '@' : operator === 'in' ? '#' : '';
  return `${result.replace(/\s{2,}/g, ' ').trim()} -${operator}:${sigil}${quoteIfNeeded(value)}`.trim();
}

/** True when `operator:value` is present with a leading `-`. */
export function isExcluded(
  source: string,
  operator: NegatableOperator,
  value: string,
  now: Date = new Date(),
): boolean {
  return parseSearchQuery(source, now).tokens.some(
    (token) =>
      token.negated &&
      token.operator === operator &&
      token.value.toLowerCase() === value.toLowerCase(),
  );
}

/** Adds the exclusion, or removes it if it is already there. */
export function toggleExclusion(
  source: string,
  operator: NegatableOperator,
  value: string,
  now: Date = new Date(),
): string {
  const present = parseSearchQuery(source, now).tokens.find(
    (token) =>
      token.negated &&
      token.operator === operator &&
      token.value.toLowerCase() === value.toLowerCase(),
  );
  return present ? removeSearchToken(source, present) : excludeOperator(source, operator, value, now);
}

/* ---------------------------------------------------------------------------
 * Highlighting
 * ------------------------------------------------------------------------- */

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Splits a sentinel-marked snippet into plain segments.
 *
 * No HTML crosses the wire and none is produced here — the caller renders
 * `match` segments as `<mark>` React elements, so the message body is text at
 * every step.
 */
export function splitHighlights(snippet: string): HighlightSegment[] {
  const segments: HighlightSegment[] = [];
  let index = 0;

  while (index < snippet.length) {
    const open = snippet.indexOf(HIGHLIGHT_START, index);
    if (open === -1) break;
    const close = snippet.indexOf(HIGHLIGHT_END, open + 1);
    if (close === -1) break;
    if (open > index) segments.push({ text: snippet.slice(index, open), match: false });
    segments.push({ text: snippet.slice(open + 1, close), match: true });
    index = close + 1;
  }
  if (index < snippet.length) segments.push({ text: snippet.slice(index), match: false });
  // A snippet with no sentinels at all is one unmatched segment.
  return segments.length > 0 ? segments : [{ text: snippet, match: false }];
}

/**
 * Client-side highlighter for fields the server does not headline (file names,
 * channel names, people). Case-insensitive, longest-term-first so overlapping
 * terms cannot produce interleaved fragments.
 */
export function highlightTerms(value: string, terms: readonly string[]): HighlightSegment[] {
  const usable = [...new Set(terms.map((term) => term.trim().toLowerCase()).filter((term) => term.length > 1))]
    .sort((a, b) => b.length - a.length);
  if (usable.length === 0 || value === '') return [{ text: value, match: false }];

  const lower = value.toLowerCase();
  const marked = new Array<boolean>(value.length).fill(false);
  for (const term of usable) {
    let at = lower.indexOf(term);
    while (at !== -1) {
      for (let offset = at; offset < at + term.length; offset += 1) marked[offset] = true;
      at = lower.indexOf(term, at + term.length);
    }
  }

  const segments: HighlightSegment[] = [];
  let start = 0;
  for (let index = 1; index <= value.length; index += 1) {
    if (index === value.length || marked[index] !== marked[start]) {
      segments.push({ text: value.slice(start, index), match: marked[start] === true });
      start = index;
    }
  }
  return segments;
}

/** The bare words a client-side highlighter should look for. */
export function highlightTermsOf(parsed: ParsedQuery): string[] {
  return parsed.tokens
    .filter((token) => !token.negated && (token.kind === 'term' || token.kind === 'phrase'))
    .map((token) => token.value);
}
